/**
 * The browser half of the planner round-trip.
 *
 * The model never receives the RAW file. It receives a downscaled JPEG of the
 * frame as it currently looks, the same frame with the recipe neutralised, the
 * measured statistics, and any reference images the user attached. What comes
 * back is a proposal that `validatePlan` still has to accept before it can
 * reach a pixel — so this module's only job is to move bytes and to fail in
 * language the person at the keyboard can act on.
 *
 * The API key lives on the server. Nothing here knows it, and nothing here
 * talks to a model provider directly.
 */

import type { CaptureFacts, ChatMessage, ChatRole, SourceKind } from '@lumos/core';

export type ReferenceMode = 'inspire' | 'match';

/** More than three references makes the request large and the read muddy. */
export const MAX_REFERENCES = 3;

/** Longest edge of an attached reference image, in pixels. */
export const REFERENCE_EDGE = 900;

const PLAN_ENDPOINT = '/api/plan';
const HEALTH_ENDPOINT = '/api/health';

/**
 * A deployed copy can be put behind a shared code so a public URL cannot spend
 * the planner budget. It is a gate on cost, not a login: there are no accounts,
 * and it protects nothing on the photographer's own machine.
 */
const ACCESS_STORAGE_KEY = 'lumos.accessCode';

export function storedAccessCode(): string {
  try {
    return window.localStorage.getItem(ACCESS_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function storeAccessCode(code: string): void {
  try {
    if (code) window.localStorage.setItem(ACCESS_STORAGE_KEY, code);
    else window.localStorage.removeItem(ACCESS_STORAGE_KEY);
  } catch {
    // A browser with storage disabled simply asks again next time.
  }
}

/** Vision models with a long prompt can genuinely take a minute or two. */
const PLAN_TIMEOUT_MS = 180_000;

const UNREACHABLE =
  'Could not reach the Lumos server. Check that it is running and that the app is proxying /api to it.';

const TIMED_OUT =
  'The planner did not answer within three minutes, so the request was cancelled. Try again, or ask for something narrower.';

/** Capture facts as the planner receives them: EXIF plus what the decoder found. */
export interface PlanCapture extends CaptureFacts {
  filename: string;
  /** The same strip the histogram shows: "1/125  ·  ƒ/2.0  ·  ISO 400  ·  85mm". */
  summary: string;
  fullWidth: number;
  fullHeight: number;
  source: SourceKind;
  /** True when the pixels came from an embedded preview rather than the sensor. */
  fromEmbeddedPreview: boolean;
  asShot: { kelvin: number; tint: number; estimated: boolean };
}

/** One earlier turn, compressed to what the planner can actually use. */
export interface PlanHistoryTurn {
  role: ChatRole;
  text: string;
  /** Registry ids proposed on that turn, when it carried a plan. */
  proposed?: string[];
  /** What the user did with that proposal. */
  disposition?: ChatMessage['disposition'];
}

/**
 * Which planner to use. The quick one answers in roughly nine seconds; the
 * considered one takes about half a minute and reasons harder about why the
 * frame looks the way it does. The server holds the actual model ids.
 */
export type PlannerTier = 'quick' | 'considered';

export interface PlanRequest {
  tier: PlannerTier;
  message: string;
  /** JPEG data URL of the frame as currently edited. */
  imageDataUrl: string;
  /** JPEG data URL of the same frame with the recipe neutralised. */
  originalDataUrl: string;
  referenceDataUrls: string[];
  referenceMode: ReferenceMode;
  /** Output of `summariseStatsForPlanner`, or null if no frame has been measured. */
  stats: Record<string, unknown> | null;
  /** Output of `describeActiveRecipe`: only the controls that are off neutral. */
  activeRecipe: Record<string, unknown>;
  capture: PlanCapture | null;
  history: PlanHistoryTurn[];
}

export interface PlanResponse {
  /** Unvalidated planner payload. Pass it straight to `validatePlan`. */
  raw: unknown;
  /** Model the server actually used, as it reported it. */
  model: string;
  /** Round trip measured in the browser, including upload of the proxy images. */
  latencyMs: number;
}

export interface HealthReport {
  /** The server answered. */
  reachable: boolean;
  /** This deployment wants a shared access code before it will plan. */
  requiresAccessCode: boolean;
  /** The server says it can reach a model — i.e. it has an OpenRouter key. */
  configured: boolean;
  model: string | null;
  /** Extra detail worth showing, when the server or the network offered one. */
  detail: string | null;
}

/** A failure the user can be told about verbatim. */
export class PlanRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'PlanRequestError';
    this.status = status;
  }
}

/**
 * A plan body is recognised by carrying at least one of the fields the planner
 * contract defines. Checking the shape rather than trusting a key name is what
 * turns a mismatched envelope into a visible error instead of a proposal with
 * nothing in it.
 */
function isPlanShaped(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.operations) ||
    typeof candidate.summary === 'string' ||
    Array.isArray(candidate.warnings)
  );
}

export async function requestPlan(input: PlanRequest): Promise<PlanResponse> {
  const started = performance.now();

  let response: Response;
  try {
    response = await fetch(PLAN_ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        ...(storedAccessCode() ? { 'x-lumos-access': storedAccessCode() } : {}),
      },
      body: JSON.stringify(input),
      signal: AbortSignal.timeout(PLAN_TIMEOUT_MS),
    });
  } catch (error) {
    if (isTimeout(error)) throw new PlanRequestError(TIMED_OUT);
    throw new PlanRequestError(`${UNREACHABLE} (${describeError(error)})`);
  }

  const { json, text } = await readPayload(response);
  const detail = detailOf(json, text);

  if (!response.ok) {
    throw new PlanRequestError(messageForStatus(response.status, detail), response.status);
  }

  // A 200 that carries an error field is still a failure.
  if (json && typeof json.error === 'string' && json.error.trim().length > 0) {
    throw new PlanRequestError(
      `The planner reported a problem: ${detail ?? json.error.trim()}`,
      response.status,
    );
  }

  if (!json) {
    throw new PlanRequestError(
      text.length > 0
        ? `The server answered with something that is not JSON: “${snippet(text)}”. That usually means a proxy or a different server answered instead of the Lumos API.`
        : 'The server answered with an empty body, so there was no plan to read.',
      response.status,
    );
  }

  // `/api/plan` answers `{ ok, raw, model, latencyMs }`, where `raw` is the
  // model's own object, deliberately unvalidated by the server so that
  // `validatePlan` runs here against the recipe the plan will actually land on.
  // `plan` and a flat body are accepted too, so an older or proxied server does
  // not silently produce an empty proposal.
  const envelope = json as Record<string, unknown>;
  const wrapped = ['raw', 'plan'].map((key) => envelope[key]).find(isPlanShaped);
  const raw = wrapped ?? (isPlanShaped(envelope) ? envelope : null);

  if (raw === null) {
    throw new PlanRequestError(
      'The server answered without a plan in it. That usually means it is running a different version than this page.',
      response.status,
    );
  }

  return {
    raw,
    model: typeof json.model === 'string' && json.model.length > 0 ? json.model : 'an unnamed model',
    latencyMs: Math.round(performance.now() - started),
  };
}

export async function checkHealth(): Promise<HealthReport> {
  let response: Response;
  try {
    response = await fetch(HEALTH_ENDPOINT, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    });
  } catch (error) {
    return {
      reachable: false,
      configured: false,
      requiresAccessCode: false,
      model: null,
      detail: isTimeout(error) ? 'The server did not answer.' : UNREACHABLE,
    };
  }

  const { json, text } = await readPayload(response);
  const model = typeof json?.model === 'string' && json.model.length > 0 ? json.model : null;

  if (!response.ok) {
    return {
      reachable: true,
      configured: false,
      requiresAccessCode: json?.requiresAccessCode === true,
      model,
      detail: detailOf(json, text) ?? messageForStatus(response.status, null),
    };
  }

  // `/api/health` answers `{ ok, model, hasKey, tiers, pinned }`: `hasKey` is
  // the flag that says whether the server can actually reach a model. A server
  // that omits it but names a model is treated as configured, and an explicit
  // `false` — under either name — is always believed.
  const configured =
    json !== null && json.ok !== false && json.hasKey !== false && json.configured !== false;

  return {
    reachable: true,
    configured,
    requiresAccessCode: json?.requiresAccessCode === true,
    model,
    detail: typeof json?.detail === 'string' ? json.detail : null,
  };
}

/**
 * Downscales an image file to a JPEG data URL for use as a reference.
 * Reference images are only ever read as pixels; the file itself never leaves
 * the browser.
 */
export async function fileToDataUrl(file: File, maxEdge: number): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      `Could not read ${file.name} as an image. Reference images need to be JPEG, PNG, WebP or AVIF.`,
    );
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    if (typeof OffscreenCanvas === 'function') {
      const canvas = new OffscreenCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser would not give Lumos a 2D drawing context.');
      context.drawImage(bitmap, 0, 0, width, height);
      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.86 });
      return await blobToDataUrl(blob);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser would not give Lumos a 2D drawing context.');
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', 0.86);
  } finally {
    bitmap.close();
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('The browser could not encode the image.'));
    reader.readAsDataURL(blob);
  });
}

/** Reads a response body once, as JSON when it parses and as text when it does not. */
async function readPayload(
  response: Response,
): Promise<{ json: Record<string, unknown> | null; text: string }> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return { json: null, text: '' };
  }

  const trimmed = text.trim();
  if (trimmed.length === 0) return { json: null, text: '' };

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return { json: parsed as Record<string, unknown>, text: trimmed };
    }
  } catch {
    // Falls through: an HTML error page or a proxy banner, not a plan.
  }
  return { json: null, text: trimmed };
}

function detailOf(json: Record<string, unknown> | null, text: string): string | null {
  if (json) {
    for (const key of ['error', 'message', 'detail'] as const) {
      const value = json[key];
      if (typeof value === 'string' && value.trim().length > 0) return value.trim();
    }
    return null;
  }
  return text.length > 0 ? snippet(text) : null;
}

function messageForStatus(status: number, detail: string | null): string {
  const tail = detail ? ` ${detail}` : '';
  switch (status) {
    case 401:
      return detail ?? 'This copy of Lumos needs an access code before the assistant will answer.';
    case 403:
      return `The model provider rejected the server's credentials (HTTP ${status}). Check that OPENROUTER_API_KEY is valid and has credit.${tail}`;
    case 404:
      return 'The server has no /api/plan endpoint. Either the Lumos server is not running behind this app, or the dev proxy points somewhere else.';
    case 408:
    case 504:
      return `The model did not answer in time (HTTP ${status}). Try again, or ask for something narrower.${tail}`;
    case 413:
      return 'The request was too large for the server. Remove a reference image and send it again.';
    case 429:
      return `The model provider is rate-limiting this key. Wait a few seconds and send it again.${tail}`;
    case 503:
      return `The server has no OpenRouter key configured, so it cannot ask a model anything. Set OPENROUTER_API_KEY where the server runs and restart it.${tail}`;
    default:
      break;
  }
  if (status >= 500) return `The server failed while planning (HTTP ${status}).${tail}`;
  return `The server rejected this request (HTTP ${status}).${tail}`;
}

/** Strips markup and collapses whitespace so an HTML error page stays readable. */
function snippet(text: string): string {
  const flattened = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return flattened.length > 180 ? `${flattened.slice(0, 180)}…` : flattened;
}

function isTimeout(error: unknown): boolean {
  return (
    error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')
  );
}

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) return error.message;
  return 'network error';
}
